// src/orders/ExtendedDashboardBox.tsx
import { Box, Grid, Skeleton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Variant } from '@mui/material/styles/createTypography';
import { usePriceFormatter } from '../../common/usePriceFormatter';
import { DailyAvgRow } from './DailyAvgRow';
import { ComparisonBadge } from './ComparisonBadge';
import { calculateDailyAverage, calculatePercentageChange, getDaysInfo } from './dashboardComparisonUtils';

type ExtendedDashboardBoxProps = {
  isLoading: boolean;
  title: string;
  today?: number;
  yesterday?: number;
  month?: number;
  previousMonth?: number;
  sameMonthLastYear?: number;
  isCurrency?: boolean;
  emphasized?: boolean;
};

export const ExtendedDashboardBox = ({
  isLoading,
  title,
  today,
  yesterday,
  month,
  previousMonth,
  sameMonthLastYear,
  isCurrency,
  emphasized = false,
}: ExtendedDashboardBoxProps) => {
  const { t } = useTranslation();
  const priceFormatter = usePriceFormatter();

  const formatValue = (value?: number, variant: Variant = 'h6') => {
    if (value === undefined) {
      return '0';
    }

    if (isCurrency) {
      return priceFormatter(value, false, variant);
    }
    return (
      <Typography variant={variant} dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value.toLocaleString()}
      </Typography>
    );
  };

  const daysInfo = getDaysInfo();
  const currentAvg = calculateDailyAverage(month, daysInfo.dayOfMonth);
  const prevMonthAvg = calculateDailyAverage(previousMonth, daysInfo.daysInPreviousMonth);
  const lastYearAvg = calculateDailyAverage(sameMonthLastYear, daysInfo.daysInSameMonthLastYear);

  const showPercentage = daysInfo.dayOfMonth >= 15;
  const projected = showPercentage && month ? month * (daysInfo.daysInCurrentMonth / daysInfo.dayOfMonth) : 0;

  const prevMonthChange =
    showPercentage && previousMonth !== undefined ? calculatePercentageChange(projected, previousMonth) : null;
  const lastYearChange =
    showPercentage && sameMonthLastYear !== undefined ? calculatePercentageChange(projected, sameMonthLastYear) : null;

  const isPrevMonthNew = showPercentage && previousMonth === 0 && (month || 0) > 0;
  const isLastYearNew = showPercentage && sameMonthLastYear === 0 && (month || 0) > 0;

  return (
    <Box
      sx={{
        border: emphasized ? '2px solid' : '1px solid',
        borderColor: emphasized ? 'primary.main' : 'divider',
        backgroundColor: emphasized ? 'primary.light' : 'background.paper',
        borderRadius: '8px',
        p: 2,
        minWidth: 280,
        '& .MuiTypography-subtitle1': {
          color: emphasized ? 'primary.main' : 'text.secondary',
          fontWeight: emphasized ? 700 : 400,
        },
        '& .MuiTypography-h4': {
          color: emphasized ? 'primary.main' : 'inherit',
          fontWeight: emphasized ? 700 : undefined,
        },
      }}>
      <Grid container direction="column" gap={1}>
        <Typography variant="subtitle1" color="text.secondary">
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton variant="rectangular" width={100} height={40} />
        ) : (
          <>
            {formatValue(today, 'h4')}
            <Grid container gap={0.5} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.yesterday')}:
              </Typography>
              {formatValue(yesterday, 'body2')}
              <Grid mx={1}>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
              </Grid>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.thisMonth')}:
              </Typography>
              {formatValue(month, 'body2')}
              <Grid mx={1}>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
              </Grid>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.prevMonth')}:
              </Typography>
              {formatValue(previousMonth, 'body2')}
            </Grid>
            {showPercentage && (
              <DailyAvgRow
                currentAvg={currentAvg}
                prevMonthAvg={prevMonthAvg}
                lastYearAvg={lastYearAvg}
                lastYearLabel={daysInfo.sameMonthLastYearLabel}
              />
            )}
            {showPercentage && (
              <Grid container gap={1} flexWrap="wrap">
                {prevMonthChange && (
                  <ComparisonBadge
                    percentage={prevMonthChange.percentage}
                    direction={prevMonthChange.direction}
                    label={t('dashboard.vsPrevMonth')}
                    isNew={isPrevMonthNew}
                  />
                )}
                {lastYearChange && (
                  <ComparisonBadge
                    percentage={lastYearChange.percentage}
                    direction={lastYearChange.direction}
                    label={`${t('dashboard.vsLastYear')} ${daysInfo.sameMonthLastYearLabel}`}
                    isNew={isLastYearNew}
                  />
                )}
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};
